import { type FunctionComponent, type HTMLAttributes, useEffect } from 'react';
import { type PartialPick } from '../../../../../@types/build';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { useExpandableBehavior } from '../../../hooks/expandable/expandable.hooks';
import { ClickableElement } from '../../buttons';
import AddIcon from '../../svgs/add.svg';
import ChevronDownIcon from '../../svgs/chevron-down.svg';
import ChevronUpIcon from '../../svgs/chevron-up.svg';
import RemoveIcon from '../../svgs/remove.svg';
import { Badge } from '../badge/badge.component';

type IndicatorPositions = 'left' | 'right';
type IndicatorStyles = 'plus' | 'chevron';
type IndicatorColors = 'white' | 'black' | 'grey';

export type ExpandablePanelProps = {
	children: JSX.Element | JSX.Element[];
	containerClassName?: string;
	heading?: string | JSX.Element;
	forceExpand?: boolean;
	startExpanded?: boolean;
	onExpand?: Function;
	onCollapse?: Function;
	renderCollapsedChildren?: boolean;
	borderRadius?: string;
	badgeCount?: number;
	automationHook?: string;
	indicatorPosition?: IndicatorPositions;
	indicatorStyle?: IndicatorStyles;
	indicatorColor?: IndicatorColors;
	forceClose?: boolean;
	expandedBorder?: boolean;
	disabled?: boolean;
	lightBorder?: boolean;
	headerClassName?: string;
} & PartialPick<HTMLAttributes<HTMLElement>, 'id' | 'className'>;

const EXPAND_INDICATORS_MAP: Readonly<{ [key in IndicatorStyles]: React.ReactNode }> = {
	plus: <AddIcon />,
	chevron: <ChevronDownIcon />
};

const COLLAPSE_INDICATORS_MAP: Readonly<{ [key in IndicatorStyles]: React.ReactNode }> = {
	plus: <RemoveIcon />,
	chevron: <ChevronUpIcon />
};

const Indicator: FunctionComponent<{ indicatorStyle: IndicatorStyles; isExpanded: boolean }> = ({ indicatorStyle, isExpanded }) => {
	const expandIcon = EXPAND_INDICATORS_MAP[indicatorStyle];
	const collapseIcon = COLLAPSE_INDICATORS_MAP[indicatorStyle];

	return (
		<div data-testid="indicator" className="f-inherit lh-solid ml3 flex items-center" data-automation="expand">
			{isExpanded ? collapseIcon : expandIcon}
		</div>
	);
};

export const ExpandablePanel: FunctionComponent<ExpandablePanelProps> = ({
	children,
	id,
	expandedBorder = true,
	className = 'f3',
	containerClassName = 'pa3',
	renderCollapsedChildren = false,
	heading,
	onExpand,
	onCollapse,
	borderRadius,
	badgeCount = 0,
	forceExpand = false,
	startExpanded = false,
	automationHook,
	indicatorPosition = 'left',
	indicatorStyle = 'plus',
	indicatorColor = 'black',
	forceClose = false,
	disabled = false,
	lightBorder = false,
	headerClassName = ''
}) => {
	const { isExpanded, toggle, handleKeyPress, handleClick } = useExpandableBehavior({
		startExpanded,
		onExpand,
		onCollapse
	});

	const childrenDisplayClass = !isExpanded ? 'dn' : `db ${containerClassName}`;
	const shouldRenderChildren = isExpanded || renderCollapsedChildren;

	useEffect(() => {
		if (forceExpand && !isExpanded) {
			toggle();
		}
		if (forceClose && isExpanded) {
			toggle();
		}
	}, [forceExpand, forceClose, isExpanded, toggle]);

	return (
		<section
			id={id}
			className={`w-100 ba ${borderRadius ? borderRadius : 'br1'} ${
				lightBorder ? 'b--theme-grey-lighter' : 'b--theme-grey-light'
			} ${className}`}
			role="tabpanel"
			data-automation={generateDataSelector('panel', automationHook)}>
			<ClickableElement
				className={`flex ${
					indicatorPosition === 'right' ? 'justify-between mh2 pl0 pr3' : ''
				} items-center pv3 w-100 f-inherit theme-${indicatorColor}`}
				onClick={handleClick}
				tabIndex={0}
				onKeyDown={handleKeyPress}
				ariaLabel={`Expand ${heading} Section`}
				disabled={disabled}>
				{indicatorPosition === 'right' ? (
					<>
						<div className="flex items-center">
							<h2 className={`mv0 mh2 lh-title f-inherit ${headerClassName}`}>{heading}</h2>
							<Badge count={badgeCount} />
						</div>
						<div className="mr2">
							<Indicator isExpanded={isExpanded} indicatorStyle={indicatorStyle} />
						</div>
					</>
				) : (
					<>
						<Indicator isExpanded={isExpanded} indicatorStyle={indicatorStyle} />
						<h2 className={`mv0 mh2 lh-title f-inherit ${headerClassName}`}>{heading}</h2>
						<Badge count={badgeCount} />
					</>
				)}
			</ClickableElement>
			{isExpanded && <div className={`mh3 ${expandedBorder ? 'bb b--theme-grey-light' : ''}`} />}
			<div className={childrenDisplayClass}>{shouldRenderChildren && children}</div>
		</section>
	);
};
